// App.jsx — URL-based routing using browser History API (no react-router needed)
//
// Public routes:   /  /about  /products  /products/:slug  /contact  /privacy  /terms
// Admin route:     /admin  (no Navbar/Footer)
//
// Clicking any nav link calls navigate(path) which:
//   1. Pushes to browser history → URL changes
//   2. Updates React state → component re-renders
// Browser back/forward buttons work via popstate listener.

import {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from "react";
import "./styles/globals.css";
import cfg from "./config";
import { t as translate } from "./utils/data";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import {
  HomePage,
  AboutPage,
  ProductsPage,
  ProductDetailPage,
  ContactPage,
  SimplePage,
} from "./pages/PublicPages";
import AdminPage from "./pages/AdminPage";

// ─── Language Context ──────────────────────────────────────────────────────────
const LangCtx = createContext(null);
export const useLang = () => useContext(LangCtx);

// ─── Router Context ────────────────────────────────────────────────────────────
const RouterCtx = createContext(null);
export const useRouter = () => useContext(RouterCtx);

// ─── WhatsApp FAB ──────────────────────────────────────────────────────────────
const FAB = () => (
  <a
    href={`https://wa.me/${cfg.whatsapp}`}
    target="_blank"
    rel="noreferrer"
    style={{
      position: "fixed",
      bottom: 24,
      right: 24,
      zIndex: 800,
      width: 52,
      height: 52,
      borderRadius: "50%",
      background: "linear-gradient(135deg,#25d366,#128C7E)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 8px 24px rgba(37,211,102,.45)",
      fontSize: 24,
      transition: "all .3s",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.12)")}
    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
  >
    💬
  </a>
);

// ─── Legal content ─────────────────────────────────────────────────────────────
const h2 = (s) =>
  `<h2 style="font-family:'DM Serif Display',serif;font-size:24px;color:#0d1f12;margin-bottom:14px;">${s}</h2>`;
const h3 = (s) =>
  `<h3 style="font-family:'DM Serif Display',serif;font-size:18px;color:#0d1f12;margin:20px 0 9px;">${s}</h3>`;
const p = (s) => `<p style="margin-bottom:12px;">${s}</p>`;
const PRIVACY = `${h2("Privacy Policy")}${p(`${cfg.companyFull} is committed to protecting your privacy.`)}${h3("Information We Collect")}${p("We collect your name, phone number, email address, and messages when you submit an inquiry.")}${h3("How We Use It")}${p("We use your information to respond to inquiries. We do not sell your data to third parties.")}${h3("Contact")}<p>Email: <strong>${cfg.email}</strong> &nbsp;|&nbsp; Phone: <strong>${cfg.phone}</strong></p>`;
const TERMS = `${h2("Terms &amp; Conditions")}${p(`By using this website you agree to the terms set forth by ${cfg.companyFull}.`)}${h3("Product Information")}${p("Seed performance may vary based on soil conditions, climate, and cultivation practices.")}${h3("Returns &amp; Refunds")}${p("We accept returns of unopened seed packets within 7 days with valid proof of purchase.")}${h3("Governing Law")}${p("These terms are governed by the laws of Gujarat, India.")}`;

// ─── Route helpers ─────────────────────────────────────────────────────────────
// Parse current window.location.pathname into { page, slug }
function parsePath(pathname = window.location.pathname) {
  const path = pathname.replace(/\/+$/, "") || "/"; // strip trailing slashes
  if (path === "/" || path === "") return { page: "home", slug: null };
  if (path === "/about") return { page: "about", slug: null };
  if (path === "/products") return { page: "products", slug: null };
  if (path.startsWith("/products/"))
    return { page: "product", slug: path.split("/products/")[1] };
  if (path === "/contact") return { page: "contact", slug: null };
  if (path === "/privacy") return { page: "privacy", slug: null };
  if (path === "/terms") return { page: "terms", slug: null };
  if (path === "/admin" || path.startsWith("/admin"))
    return { page: "admin", slug: null };
  return { page: "home", slug: null };
}

// ─── App root ──────────────────────────────────────────────────────────────────
const LANG_KEY = `${cfg.skuPrefix.toLowerCase()}_lang`;

export default function App() {
  const [lang, setLang] = useState(
    () => localStorage.getItem(LANG_KEY) || "en",
  );
  const [route, setRoute] = useState(() => parsePath());
  const [product, setProduct] = useState(null); // full product object for detail page

  // ── Language ──────────────────────────────────────────────────────────────
  const changeLang = (l) => {
    setLang(l);
    localStorage.setItem(LANG_KEY, l);
  };
  const t = (key) => translate(lang, key);

  // ── Navigation ─────────────────────────────────────────────────────────────
  // navigate(path) — changes URL + React state
  const navigate = useCallback((path) => {
    window.history.pushState({}, "", path);
    setRoute(parsePath(path));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // navigateTo(page) — convenience helper used by all components
  // page: "home" | "about" | "products" | "contact" | "privacy" | "terms" | "admin"
  const navigateTo = useCallback(
    (page, opts = {}) => {
      const MAP = {
        home: "/",
        about: "/about",
        products: "/products",
        contact: "/contact",
        privacy: "/privacy",
        terms: "/terms",
        admin: "/admin",
      };
      if (page === "product-detail" && opts.product) {
        setProduct(opts.product);
        navigate(`/products/${opts.product.slug || opts.product.id}`);
      } else {
        if (page !== "product-detail") setProduct(null);
        navigate(MAP[page] || "/");
      }
    },
    [navigate],
  );

  // Handle browser back / forward buttons
  useEffect(() => {
    const onPop = () => setRoute(parsePath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // ── Page resolver ──────────────────────────────────────────────────────────
  const renderPage = () => {
    const { page, slug } = route;

    // setPage compatibility — components call setPage(key) or setPage(key, product)
    const setPage = (key, prod) => {
      if (key === "product-detail" && prod) {
        navigateTo("product-detail", { product: prod });
      } else {
        navigateTo(key);
      }
    };
    const setPageWithProduct = (p) =>
      navigateTo("product-detail", { product: p });

    switch (page) {
      case "home":
        return <HomePage setPage={setPage} setProduct={setPageWithProduct} />;
      case "about":
        return <AboutPage />;
      case "products":
        return (
          <ProductsPage setPage={setPage} setProduct={setPageWithProduct} />
        );
      case "product":
        return (
          <ProductDetailPage
            product={product}
            setPage={setPage}
            setProduct={setPageWithProduct}
          />
        );
      case "contact":
        return <ContactPage />;
      case "privacy":
        return <SimplePage title={t("privacy")} content={PRIVACY} />;
      case "terms":
        return <SimplePage title={t("terms")} content={TERMS} />;
      case "admin":
        return (
          <AdminPage setPage={(p) => navigate(p === "home" ? "/" : `/${p}`)} />
        );
      default:
        return <HomePage setPage={setPage} setProduct={setPageWithProduct} />;
    }
  };

  const isAdmin = route.page === "admin";
  // Current page name for Navbar active state
  const currentPage = route.page === "product" ? "products" : route.page;

  return (
    <RouterCtx.Provider value={{ route, navigate, navigateTo }}>
      <LangCtx.Provider value={{ lang, t, setLang: changeLang }}>
        {isAdmin ? (
          // Admin — no Navbar or Footer
          renderPage()
        ) : (
          <>
            <Navbar page={currentPage} setPage={(p) => navigateTo(p)} />
            <main>{renderPage()}</main>
            <Footer setPage={(p) => navigateTo(p)} />
            <FAB />
          </>
        )}
      </LangCtx.Provider>
    </RouterCtx.Provider>
  );
}
